import type { ResourceAmounts } from '../buildings/definitions';
import type { ArmorClass, DamageType } from '../combat/rules';

export type UnitTypeId = 'ranger' | 'heavy' | 'engineer' | 'sniper' | 'flamer' | 'walker' | 'runner' | 'brute' | 'bloater' | 'screamer';

export interface UnitDefinition {
  readonly id: UnitTypeId;
  readonly name: string;
  readonly role: string;
  readonly maxHealth: number;
  /** Tiles per second. */
  readonly speed: number;
  /** Attack reach in tiles (centre to target). */
  readonly range: number;
  readonly damage: number;
  readonly damageType: DamageType;
  /** Seconds between attacks. */
  readonly cooldown: number;
  /** How far it notices enemies (tiles) when idle or attack-moving. */
  readonly sight: number;
  readonly armorClass: ArmorClass;
  /** Flat damage removed from every hit it takes (after multipliers). */
  readonly armor: number;
  /** Ranged units fire projectiles at this speed (tiles/s); melee units hit instantly. */
  readonly projectileSpeed?: number;
  /** Infected type (Phase 7): spawned by the infection system, never trained, never commanded by players. */
  readonly infected?: boolean;
  /** Chase leash in tiles beyond sight (default COMBAT.leashTiles). */
  readonly leash?: number;
  /** A* node budget per route (default 20 000). Infected use a small one so hundreds of them stay cheap. */
  readonly pathNodes?: number;
  /** Health per second it restores when repairing (Phase 8; Engineers). */
  readonly repairRate?: number;
  readonly cost: ResourceAmounts;
  /** Survivors taken from the colony. */
  readonly survivors: number;
  readonly trainSeconds: number;
}

/** Placeholder balance (DECISIONS D57, combat stats D63). */
const DEFS: readonly UnitDefinition[] = [
  {
    id: 'ranger',
    name: 'Ranger',
    role: 'Ranged rifle squad member. Good against the regular infected.',
    maxHealth: 60,
    speed: 2.2,
    range: 4,
    damage: 8,
    damageType: 'bullet',
    cooldown: 1,
    sight: 6,
    armorClass: 'light',
    armor: 0,
    projectileSpeed: 14,
    cost: { wood: 15, scrap: 10 },
    survivors: 1,
    trainSeconds: 12,
  },
  {
    id: 'heavy',
    name: 'Heavy',
    role: 'Armoured frontline brawler. Slow, tough and hits hard up close.',
    maxHealth: 160,
    speed: 1.4,
    range: 1.3,
    damage: 18,
    damageType: 'melee',
    cooldown: 1.5,
    sight: 5,
    armorClass: 'armored',
    armor: 3,
    cost: { wood: 20, scrap: 30 },
    survivors: 1,
    trainSeconds: 18,
  },
  {
    id: 'engineer',
    name: 'Engineer',
    role: 'Repairs your buildings (tap a damaged one, or it fixes what is nearby). Weak in a fight.',
    maxHealth: 50,
    speed: 2,
    range: 1.3,
    damage: 4,
    damageType: 'melee',
    cooldown: 1.2,
    sight: 4,
    repairRate: 8,
    armorClass: 'light',
    armor: 0,
    cost: { wood: 20, scrap: 10 },
    survivors: 1,
    trainSeconds: 14,
  },
  {
    // Season-pass unit (D112): long reach, slow fire — picks off Runners before they reach the walls.
    id: 'sniper',
    name: 'Sniper',
    role: 'Long-range marksman. Slow to fire, hits very hard; fragile up close.',
    maxHealth: 45,
    speed: 2,
    range: 8,
    damage: 34,
    damageType: 'bullet',
    cooldown: 2.4,
    sight: 9,
    armorClass: 'light',
    armor: 0,
    projectileSpeed: 24,
    cost: { wood: 20, scrap: 30 },
    survivors: 1,
    trainSeconds: 20,
  },
  {
    // Season-pass unit (D112): short-range fire, shreds crowds of infected.
    id: 'flamer',
    name: 'Flamer',
    role: 'Flamethrower. Short range, burns the infected fast; weak against armour.',
    maxHealth: 110,
    speed: 1.7,
    range: 2.4,
    damage: 9,
    damageType: 'fire',
    cooldown: 0.5,
    sight: 5,
    armorClass: 'armored',
    armor: 1,
    projectileSpeed: 10,
    cost: { wood: 25, scrap: 35 },
    survivors: 1,
    trainSeconds: 20,
  },
  {
    id: 'walker',
    name: 'Walker',
    role: 'Infected. Slow and relentless; bites spread the infection.',
    maxHealth: 80,
    speed: 0.9,
    range: 1.2,
    damage: 6,
    damageType: 'melee',
    cooldown: 1.2,
    sight: 7,
    armorClass: 'light',
    armor: 0,
    cost: {},
    survivors: 0,
    trainSeconds: 0,
    infected: true,
    leash: 6,
    pathNodes: 1200,
  },
  {
    // Phase 14 special infected: fast and fragile — they reach the walls first and punish undefended Engineers.
    id: 'runner',
    name: 'Runner',
    role: 'Infected. Fast and fragile; hunts in later hordes.',
    maxHealth: 45,
    speed: 2.1,
    range: 1.1,
    damage: 5,
    damageType: 'melee',
    cooldown: 0.8,
    sight: 9,
    armorClass: 'light',
    armor: 0,
    cost: {},
    survivors: 0,
    trainSeconds: 0,
    infected: true,
    leash: 8,
    pathNodes: 1200,
  },
  {
    // D126: a hulking infected that soaks bullets and smashes walls.
    id: 'brute',
    name: 'Brute',
    role: 'Infected. Huge and armoured; breaks walls and gates fast.',
    maxHealth: 420,
    speed: 0.7,
    range: 1.4,
    damage: 32,
    damageType: 'melee',
    cooldown: 2,
    sight: 6,
    armorClass: 'armored',
    armor: 3,
    cost: {},
    survivors: 0,
    trainSeconds: 0,
    infected: true,
    leash: 6,
    pathNodes: 1200,
  },
  {
    // D126: swollen with gas — bursts when it dies, hurting everything close by. Kill it far from your walls.
    id: 'bloater',
    name: 'Bloater',
    role: 'Infected. Bursts when killed, damaging units and buildings around it.',
    maxHealth: 70,
    speed: 0.8,
    range: 1.2,
    damage: 4,
    damageType: 'melee',
    cooldown: 1.4,
    sight: 6,
    armorClass: 'light',
    armor: 0,
    cost: {},
    survivors: 0,
    trainSeconds: 0,
    infected: true,
    leash: 6,
    pathNodes: 1200,
  },
  {
    // D126: barely fights, but its scream sends every infected nearby at what it saw.
    id: 'screamer',
    name: 'Screamer',
    role: 'Infected. Weak, but its scream draws every infected nearby to your colony.',
    maxHealth: 60,
    speed: 1.5,
    range: 1.1,
    damage: 2,
    damageType: 'melee',
    cooldown: 1.5,
    sight: 9,
    armorClass: 'light',
    armor: 0,
    cost: {},
    survivors: 0,
    trainSeconds: 0,
    infected: true,
    leash: 10,
    pathNodes: 1200,
  },
];

export const UNIT_DEFINITIONS: ReadonlyMap<UnitTypeId, UnitDefinition> = new Map(DEFS.map((d) => [d.id, d]));
export const UNIT_TYPES: readonly UnitTypeId[] = DEFS.map((d) => d.id);
/** Types players can own and train. */
export const PLAYER_UNIT_TYPES: readonly UnitTypeId[] = DEFS.filter((d) => !d.infected).map((d) => d.id);

export function unitDef(id: UnitTypeId): UnitDefinition {
  const d = UNIT_DEFINITIONS.get(id);
  if (!d) throw new Error(`Unknown unit type "${id}"`);
  return d;
}

export function isUnitTypeId(v: unknown): v is UnitTypeId {
  return typeof v === 'string' && UNIT_DEFINITIONS.has(v as UnitTypeId);
}

/** Most units a single building can have queued. */
export const TRAIN_QUEUE_LIMIT = 5;
/** Most units one move / attack command may address. */
export const MAX_UNITS_PER_COMMAND = 200;

/** Unit combat posture. Aggressive: engage enemies in sight. Hold: never move on its own; only shoot what is in range. */
export type Stance = 'aggressive' | 'hold';
export const STANCES: readonly Stance[] = ['aggressive', 'hold'];
